// ContactLogTab — matches @StatDrivenProps visual exactly
// Dark bg, green EV/DIST heat cells, pitch type chips, result badges
// LINEBRKR tab: per-batter Statcast contact log
//
// NOTE: Sample data below mirrors the @StatDrivenProps reference layout.
// To wire to live data, replace SAMPLE_DATA with:
//   GET /api/players/:id/contact-log
//   (source: pybaseball.statcast_batter(mlbam_id, start_dt, end_dt))

import React, { useState, useEffect } from 'react'
import { searchPlayers, getPlayerContactLog, getLeaderboard, todayStr } from '../api/client'

const T = {
  bg:"#0D1117", card:"#161B22", card2:"#1C2333", card3:"#0F1623",
  border:"#21262D", border2:"#30363D",
  green:"#00D97E", greenDark:"#064E3B", greenMid:"#065F46", greenLight:"#0F2D1E",
  amber:"#F59E0B", red:"#EF4444", blue:"#3B82F6",
  text:"#E6EDF3", muted:"#AEB8C6", muted2:"#8B96A6",
}

const PITCH_COLOR = {
  "4S-FB":"#3B82F6","2S-FB":"#60A5FA","SI":"#818CF8",
  "SL":"#F59E0B","CH":"#14B8A6","CB":"#A78BFA",
  "FC":"#F97316","FS":"#34D399","ST":"#FBBF24","KN":"#94A3B8",
}

const evBg = ev => {
  if (!ev) return {bg:T.card2,fg:T.muted2}
  if (ev>=108) return {bg:"#064E3B",fg:"#00D97E"}
  if (ev>=100) return {bg:"#065F46",fg:"#34D399"}
  if (ev>=95)  return {bg:"#0F2D1E",fg:"#6EE7B7"}
  if (ev>=88)  return {bg:T.card2,  fg:"#8B949E"}
  return             {bg:T.card2,  fg:"#6E7681"}
}

const distBg = d => {
  if (!d) return {bg:T.card2,fg:T.muted2}
  if (d>=390) return {bg:"#064E3B",fg:"#00D97E"}
  if (d>=350) return {bg:"#065F46",fg:"#34D399"}
  if (d>=270) return {bg:"#0F2D1E",fg:"#6EE7B7"}
  return           {bg:T.card2,  fg:"#6E7681"}
}

const laBg = la => {
  if (la>=8&&la<=32) return {bg:"#065F46",fg:"#34D399"}  // sweet spot
  if (la>32&&la<=45) return {bg:T.card2,  fg:"#F59E0B"}  // high fly
  return                   {bg:T.card2,  fg:"#6E7681"}
}

const RESULT = {
  HR:  {bg:"#064E3B",fg:"#00D97E",bold:true},
  "2B":{bg:"#1E3A5F",fg:"#3B82F6",bold:true},
  "1B":{bg:T.card2,  fg:"#8B949E",bold:false},
  OUT: {bg:T.card3,  fg:"#6E7681",bold:false},
  BB:  {bg:"#2D1F00",fg:"#F59E0B",bold:false},
}

const TRAJ = {
  FB:{fg:"#F59E0B"},GB:{fg:"#6E7681"},LD:{fg:"#00D97E"},PU:{fg:"#6E7681"},
}

// ── Sample data matching the @StatDrivenProps reference exactly ──────────────
export const CONTACT_SAMPLE = {
  "Kyle Stowers": {
    team:"MIA", hand:"LHB", bats:"L",
    log:[
      {date:"6/15",pitcher:"Z. Wheeler", lr:"R",type:"4S-FB",ev:101.0,la:48,dist:317,res:"OUT",traj:"FB"},
      {date:"6/14",pitcher:"B. Bidois",  lr:"R",type:"4S-FB",ev:99.2, la:4, dist:84, res:"1B", traj:"GB"},
      {date:"6/14",pitcher:"P. Skenes",  lr:"R",type:"4S-FB",ev:97.8, la:64,dist:169,res:"OUT",traj:"PU"},
      {date:"6/12",pitcher:"B. Ashcraft",lr:"R",type:"FS",   ev:86.3, la:14,dist:182,res:"1B", traj:"LD"},
      {date:"6/12",pitcher:"B. Ashcraft",lr:"R",type:"CB",   ev:101.8,la:38,dist:374,res:"OUT",traj:"FB"},
      {date:"6/12",pitcher:"B. Ashcraft",lr:"R",type:"4S-FB",ev:95.4, la:1, dist:51, res:"1B", traj:"GB"},
      {date:"6/10",pitcher:"R. Nelson",  lr:"R",type:"FC",   ev:110.8,la:26,dist:425,res:"HR", traj:"FB"},
      {date:"6/10",pitcher:"R. Nelson",  lr:"R",type:"SL",   ev:97.7, la:-10,dist:11,res:"OUT",traj:"GB"},
      {date:"6/9", pitcher:"T. Clarke",  lr:"R",type:"CH",   ev:108.9,la:19,dist:369,res:"2B", traj:"LD"},
      {date:"6/9", pitcher:"Z. Gallen",  lr:"R",type:"CH",   ev:102.4,la:-15,dist:7, res:"OUT",traj:"GB"},
    ]
  },
  "Yordan Alvarez": {
    team:"HOU", hand:"LHB", bats:"L",
    log:[
      {date:"6/13",pitcher:"N. Cameron", lr:"R",type:"FC",   ev:112.1,la:28,dist:442,res:"HR", traj:"FB"},
      {date:"6/13",pitcher:"N. Cameron", lr:"R",type:"FC",   ev:104.8,la:22,dist:318,res:"2B", traj:"FB"},
      {date:"6/13",pitcher:"N. Cameron", lr:"R",type:"4S-FB",ev:91.2, la:4, dist:98, res:"OUT",traj:"GB"},
      {date:"6/12",pitcher:"L. Avila",   lr:"R",type:"FC",   ev:108.4,la:31,dist:418,res:"HR", traj:"FB"},
      {date:"6/12",pitcher:"L. Avila",   lr:"R",type:"4S-FB",ev:99.1, la:16,dist:244,res:"OUT",traj:"FB"},
      {date:"6/11",pitcher:"M. Cameron", lr:"R",type:"SL",   ev:88.4, la:-6,dist:28, res:"OUT",traj:"GB"},
      {date:"6/11",pitcher:"M. Cameron", lr:"R",type:"4S-FB",ev:105.7,la:24,dist:362,res:"HR", traj:"FB"},
      {date:"6/10",pitcher:"R. Nelson",  lr:"R",type:"FC",   ev:110.8,la:26,dist:425,res:"HR", traj:"FB"},
      {date:"6/10",pitcher:"R. Nelson",  lr:"R",type:"SL",   ev:97.7, la:-10,dist:11,res:"OUT",traj:"GB"},
      {date:"6/9", pitcher:"T. Clarke",  lr:"R",type:"CH",   ev:108.9,la:19,dist:369,res:"2B", traj:"LD"},
    ]
  },
  "Jackson Chourio": {
    team:"MIL", hand:"LHB", bats:"L",
    log:[
      {date:"6/13",pitcher:"A. Nola",    lr:"R",type:"4S-FB",ev:104.2,la:26,dist:388,res:"HR", traj:"FB"},
      {date:"6/13",pitcher:"A. Nola",    lr:"R",type:"CB",   ev:91.8, la:4, dist:102,res:"1B", traj:"GB"},
      {date:"6/13",pitcher:"A. Nola",    lr:"R",type:"4S-FB",ev:96.4, la:14,dist:208,res:"1B", traj:"LD"},
      {date:"6/12",pitcher:"Painter",    lr:"R",type:"4S-FB",ev:108.1,la:32,dist:421,res:"HR", traj:"FB"},
      {date:"6/12",pitcher:"Painter",    lr:"R",type:"4S-FB",ev:103.4,la:18,dist:294,res:"OUT",traj:"FB"},
      {date:"6/12",pitcher:"Painter",    lr:"R",type:"CH",   ev:99.2, la:22,dist:318,res:"2B", traj:"FB"},
      {date:"6/11",pitcher:"B. Anderson",lr:"R",type:"SL",   ev:88.1, la:-2,dist:44, res:"OUT",traj:"GB"},
      {date:"6/11",pitcher:"B. Anderson",lr:"R",type:"4S-FB",ev:101.7,la:28,dist:394,res:"HR", traj:"FB"},
      {date:"6/10",pitcher:"J. Gray",    lr:"R",type:"FC",   ev:94.2, la:8, dist:162,res:"1B", traj:"LD"},
      {date:"6/10",pitcher:"J. Gray",    lr:"R",type:"4S-FB",ev:107.8,la:34,dist:428,res:"HR", traj:"FB"},
    ]
  },
  "Aaron Judge": {
    team:"NYY", hand:"RHB", bats:"R",
    log:[
      {date:"6/13",pitcher:"K. Gausman", lr:"R",type:"4S-FB",ev:108.2,la:28,dist:412,res:"HR", traj:"FB"},
      {date:"6/13",pitcher:"K. Gausman", lr:"R",type:"SL",   ev:97.4, la:12,dist:188,res:"1B", traj:"LD"},
      {date:"6/12",pitcher:"C. Sale",    lr:"L",type:"4S-FB",ev:112.4,la:32,dist:438,res:"HR", traj:"FB"},
      {date:"6/12",pitcher:"C. Sale",    lr:"L",type:"SL",   ev:91.2, la:-4,dist:42, res:"OUT",traj:"GB"},
      {date:"6/12",pitcher:"C. Sale",    lr:"L",type:"4S-FB",ev:104.1,la:22,dist:301,res:"OUT",traj:"FB"},
      {date:"6/11",pitcher:"G. Cole",    lr:"R",type:"4S-FB",ev:88.3, la:8, dist:164,res:"1B", traj:"LD"},
      {date:"6/11",pitcher:"G. Cole",    lr:"R",type:"SL",   ev:93.1, la:-8,dist:21, res:"OUT",traj:"GB"},
      {date:"6/10",pitcher:"J. Flaherty",lr:"R",type:"4S-FB",ev:109.8,la:30,dist:421,res:"HR", traj:"FB"},
      {date:"6/10",pitcher:"J. Flaherty",lr:"R",type:"CH",   ev:96.2, la:18,dist:268,res:"OUT",traj:"FB"},
      {date:"6/10",pitcher:"J. Flaherty",lr:"R",type:"SL",   ev:101.3,la:38,dist:374,res:"2B", traj:"FB"},
    ]
  },
}

// Window-aware summary tiles. Recent windows (7/15/30) show batted-ball metrics from
// the log; Season shows contact RATE stats from the DB (no season EV/HR in the DB).
function Summary({log, win, sea}) {
  const isSeason = win==='season'
  const wlabel = isSeason ? 'Season' : `Last ${win} days`
  const pctT = v => v==null?'—':Math.round(v*100)+'%'
  const d3   = v => v==null?'—':Number(v).toFixed(3).replace(/^0/,'')
  let tiles
  if(isSeason){
    tiles = sea ? [
      ["Barrel %",     pctT(sea.barrel_pct),     (sea.barrel_pct||0)>=0.12?"#00D97E":(sea.barrel_pct||0)>=0.08?"#F59E0B":"#8B949E"],
      ["Hard Hit %",   pctT(sea.hard_hit_pct),   (sea.hard_hit_pct||0)>=0.50?"#00D97E":(sea.hard_hit_pct||0)>=0.40?"#F59E0B":"#8B949E"],
      ["Sweet Spot %", pctT(sea.sweet_spot_pct), (sea.sweet_spot_pct||0)>=0.38?"#00D97E":(sea.sweet_spot_pct||0)>=0.28?"#F59E0B":"#8B949E"],
      ["xwOBAcon",     d3(sea.xwobacon),         (sea.xwobacon||0)>=0.400?"#00D97E":(sea.xwobacon||0)>=0.350?"#34D399":"#8B949E"],
      ["ISO",          d3(sea.iso),              (sea.iso||0)>=0.200?"#00D97E":(sea.iso||0)>=0.150?"#F59E0B":"#8B949E"],
      ["xSLG",         d3(sea.xslg),             (sea.xslg||0)>=0.480?"#00D97E":(sea.xslg||0)>=0.400?"#34D399":"#8B949E"],
    ] : []
  } else {
    const bip = log.filter(e=>e.res!=="BB")
    const withEV = bip.filter(e=>e.ev>0)
    const avgEV = withEV.length ? (withEV.reduce((s,e)=>s+e.ev,0)/withEV.length).toFixed(1) : "—"
    const maxEV = withEV.length ? Math.max(...withEV.map(e=>e.ev)).toFixed(1) : "—"
    const hhPct = withEV.length ? Math.round(withEV.filter(e=>e.ev>=95).length/withEV.length*100) : 0
    const ssPct = bip.length ? Math.round(bip.filter(e=>e.la>=8&&e.la<=32).length/bip.length*100) : 0
    const hrs = log.filter(e=>e.res==="HR").length
    const xbh = log.filter(e=>["HR","2B","3B"].includes(e.res)).length
    tiles = [
      ["Avg Exit Velo", avgEV, parseFloat(avgEV)>=91?"#00D97E":parseFloat(avgEV)>=88?"#F59E0B":"#8B949E"],
      ["Max Exit Velo", maxEV, parseFloat(maxEV)>=108?"#00D97E":parseFloat(maxEV)>=100?"#34D399":"#F59E0B"],
      ["Hard Hit %",    hhPct+"%", hhPct>=50?"#00D97E":hhPct>=40?"#F59E0B":"#8B949E"],
      ["Sweet Spot %",  ssPct+"%", ssPct>=38?"#00D97E":ssPct>=28?"#F59E0B":"#8B949E"],
      ["Home Runs",     hrs,    hrs>=2?"#00D97E":hrs>=1?"#F59E0B":"#6E7681"],
      ["Extra-Base Hits", xbh,  xbh>=3?"#00D97E":xbh>=2?"#F59E0B":"#6E7681"],
    ]
  }
  return (
   <div style={{marginBottom:12}}>
    <div style={{fontSize:9,color:T.muted,fontWeight:800,letterSpacing:"0.11em",textTransform:"uppercase",marginBottom:6}}>
      {isSeason?'Contact rates · ':'Batted-ball metrics · '}<span style={{color:"#8B949E"}}>{wlabel}</span>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
      {tiles.map(([label,val,col])=>(
        <div key={label} style={{background:T.card2,border:`1px solid ${T.border2}`,borderRadius:9,padding:"8px 10px"}}>
          <div style={{fontSize:9,color:T.muted2,letterSpacing:"0.1em",marginBottom:3,textTransform:"uppercase"}}>{label}</div>
          <div style={{fontSize:17,fontWeight:900,color:col,fontFamily:"'SF Mono',monospace"}}>{val}</div>
        </div>
      ))}
    </div>
   </div>
  )
}

// Contact Score (0–100) from the batted-ball log — translates Statcast into one number
function contactScore(log){
  const bip = (log||[]).filter(e=>e.ev>0)
  if(!bip.length) return null
  const avgEV = bip.reduce((s,e)=>s+e.ev,0)/bip.length
  const maxEV = Math.max(...bip.map(e=>e.ev))
  const hh = bip.filter(e=>e.ev>=95).length/bip.length
  const sweet = bip.filter(e=>e.la>=8&&e.la<=32).length/bip.length
  // Scale each metric against the REAL MLB distribution so league-average ≈ 50 and
  // genuinely elite ≈ 90+. Centers (≈ league avg) and spreads (≈ avg → elite):
  //   avg EV   88.5 mph  (elite ~95)      hard-hit  40%  (elite ~55%)
  //   max EV   108 mph   (elite ~115)     sweet-spot 33% (elite ~43%)
  const clamp = x => Math.max(0, Math.min(100, x))
  const evS    = clamp(50 + (avgEV - 88.5) / 6.5  * 50)
  const mxS    = clamp(50 + (maxEV - 108)  / 7.0  * 50)
  const hhS    = clamp(50 + (hh    - 0.40) / 0.15 * 50)
  const ssS    = clamp(50 + (sweet - 0.33) / 0.10 * 50)
  const score = Math.round(0.45*evS + 0.20*mxS + 0.25*hhS + 0.10*ssS)
  const grade = score>=82?"Elite":score>=68?"Strong":score>=54?"Good":score>=42?"Average":"Concern"
  const xbh = log.filter(e=>["HR","2B","3B"].includes(e.res)).length
  return { score:Math.max(0,Math.min(100,score)), grade,
    summary:`${avgEV.toFixed(1)} avg EV · ${maxEV.toFixed(1)} max EV · ${Math.round(hh*100)}% hard-hit · ${xbh} XBH` }
}
// Semantic color: Elite (82+) = blue · Strong (68+) = green · Good (54+) = soft green
// · Average (42+) = amber · Concern = red. Matches the rest of the app.
const csColor = s => s>=82?"#3B82F6" : s>=68?"#00D97E" : s>=54?"#34D399" : s>=42?"#F59E0B" : "#EF4444"

export function ContactLog({playerName, data}) {
  const [pitchFilter, setPitchFilter] = useState("ALL")
  const [sortByEV, setSortByEV] = useState(false)

  // Window toggle: 7 / 15 / 30 days (batted-ball log) + Season (rate stats).
  const [win, setWin] = useState(15)          // 7 | 15 | 30 | 'season'
  const [winLog, setWinLog] = useState(null)
  const pid = data?.player_id
  useEffect(()=>{
    // 15 uses the log already loaded; season keeps the 15d log for the table but shows
    // rate tiles. 7 & 30 re-fetch the batted-ball log for that window.
    if(win===15 || win==='season' || !pid){ setWinLog(null); return }
    let live=true
    getPlayerContactLog(pid, win, 60).then(d=>{ if(live) setWinLog(Array.isArray(d?.log)?d.log:[]) }).catch(()=>{ if(live) setWinLog([]) })
    return ()=>{ live=false }
  },[win, pid])
  // Defensive: never crash if a caller passes an object without a `log` array
  const baseLog = (data && Array.isArray(data.log)) ? data.log : []
  const log = (winLog!=null) ? winLog : baseLog   // windowed batted-ball log
  const pitchTypes = ["ALL",...new Set(log.map(e=>e.type))]
  const cs = contactScore(log)              // recent form over the selected window
  const sea = data?.season_contact || null  // stable season contact grade + rates (DB)
  const head = sea || cs                     // headline: prefer the stable season grade

  const rows = log
    .filter(e => pitchFilter==="ALL" || e.type===pitchFilter)
    .sort((a,b) => sortByEV ? b.ev-a.ev : 0)

  return (
    <div style={{background:T.bg,borderRadius:12,overflow:"hidden"}}>

      {/* Contact read — stable season grade headline + recent-form line */}
      {head && (
        <div style={{display:"flex",alignItems:"center",gap:14,padding:"12px 16px",background:"#0F2D1E",borderBottom:`1px solid ${T.greenMid}`}}>
          <div style={{textAlign:"center",flexShrink:0}}>
            <div style={{fontSize:26,fontWeight:900,color:csColor(head.score),fontFamily:"'SF Mono',monospace",lineHeight:1}}>{head.score}</div>
            <div style={{fontSize:8.5,color:T.muted2,letterSpacing:"0.1em",marginTop:2,textTransform:"uppercase"}}>{sea?"Season":"Contact"}</div>
          </div>
          <div style={{minWidth:0}}>
            <div style={{fontSize:13,fontWeight:800,color:csColor(head.score)}}>{head.grade} contact</div>
            {sea && <div style={{fontSize:11,color:T.muted,marginTop:2,lineHeight:1.4}}>{Math.round(sea.barrel_pct*100)}% barrel · {Math.round(sea.hard_hit_pct*100)}% hard-hit · {Number(sea.xwobacon).toFixed(3)} xwOBAcon</div>}
            {cs && win!=='season' && <div style={{fontSize:11,color:T.muted2,marginTop:3,lineHeight:1.4}}>Recent {win}d: <b style={{color:csColor(cs.score)}}>{cs.score} {cs.grade}</b> · {cs.summary}</div>}
          </div>
        </div>
      )}

      {/* Player header */}
      <div style={{
        padding:"14px 16px 12px",
        borderBottom:`1px solid ${T.border}`,
        background:T.card,
      }}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:T.text,letterSpacing:"-0.02em"}}>
              {playerName}
              <span style={{
                marginLeft:8,fontSize:12,fontWeight:600,
                color:data.bats==="L"?"#3B82F6":"#F59E0B",
                background:data.bats==="L"?"#1E3A5F22":"#2D1F0022",
                border:`1px solid ${data.bats==="L"?"#3B82F655":"#F59E0B55"}`,
                borderRadius:4,padding:"1px 5px",verticalAlign:"middle",
              }}>({data.hand})</span>
            </div>
            <div style={{fontSize:12,color:T.muted,marginTop:2}}>{data.team}</div>
          </div>
          {/* Result pill counts */}
          <div style={{display:"flex",gap:6}}>
            {[["HR",T.green],["2B",T.blue],["1B",T.muted]].map(([res,col])=>{
              const count = log.filter(e=>e.res===res).length
              return count>0 ? (
                <div key={res} style={{
                  fontSize:11,fontWeight:700,
                  color:col,background:`${col}18`,
                  border:`1px solid ${col}44`,
                  borderRadius:6,padding:"3px 8px",
                }}>
                  {count} {res}
                </div>
              ) : null
            })}
          </div>
        </div>
      </div>

      <div style={{padding:"12px 14px"}}>
        {/* Window toggle — 7 / 15 / 30 days of batted balls, or season rate stats */}
        <div style={{display:"flex",gap:6,marginBottom:10}}>
          {[[7,"L7"],[15,"L15"],[30,"L30"],["season","Season"]].map(([w,lbl])=>{
            const on = win===w
            return (
              <button key={lbl} onClick={()=>setWin(w)} style={{
                flex:1,padding:"6px 0",borderRadius:8,fontSize:11.5,fontWeight:800,cursor:"pointer",
                background:on?`${T.green}22`:T.card2,border:`1px solid ${on?T.green:T.border2}`,
                color:on?T.green:T.muted2,
              }}>{lbl}</button>
            )
          })}
        </div>
        <Summary log={log} win={win} sea={sea}/>
        {win==='season' && <div style={{fontSize:9.5,color:T.muted2,margin:"-4px 0 12px",lineHeight:1.4}}>Season contact rates. The batted-ball table below shows recent balls (last 15 days).</div>}

        {/* Pitch type filter */}
        <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch"}}>
          {pitchTypes.map(pt=>{
            const ptc = pt==="ALL" ? T.green : (PITCH_COLOR[pt]||T.muted)
            const active = pitchFilter===pt
            return (
              <button key={pt} onClick={()=>setPitchFilter(pt)} style={{
                flexShrink:0,padding:"4px 11px",borderRadius:20,
                background: active ? `${ptc}33` : T.card2,
                border: `1px solid ${active ? ptc : T.border2}`,
                color: active ? ptc : T.muted2,
                fontSize:11,fontWeight:700,cursor:"pointer",
              }}>
                {pt==="ALL"?"All":pt}
              </button>
            )
          })}
          <button onClick={()=>setSortByEV(s=>!s)} style={{
            flexShrink:0,marginLeft:"auto",
            padding:"4px 11px",borderRadius:20,
            background:sortByEV?`${T.green}22`:T.card2,
            border:`1px solid ${sortByEV?T.green:T.border2}`,
            color:sortByEV?T.green:T.muted2,
            fontSize:11,fontWeight:700,cursor:"pointer",
          }}>
            {sortByEV?"EV ↓":"Sort EV"}
          </button>
        </div>

        {/* Table — horizontally scrollable so all 9 columns stay readable on mobile */}
        <div style={{borderRadius:8,overflow:"hidden",border:`1px solid ${T.border2}`}}>
         <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"thin"}}>
          <div style={{minWidth:524}}>
          {/* Header */}
          <div style={{
            display:"grid",
            gridTemplateColumns:"56px 1fr 28px 64px 64px 50px 56px 50px 44px",
            background:"#0C1F18",
            padding:"7px 0",
          }}>
            {["DATE","PITCHER","L/R","TYPE","EV","LA","DIS","RES","TRAJ"].map(h=>(
              <div key={h} style={{
                textAlign:"center",
                fontSize:9,fontWeight:700,
                color:T.green,
                letterSpacing:"0.1em",
                textTransform:"uppercase",
                fontFamily:"'SF Mono',monospace",
              }}>{h}</div>
            ))}
          </div>

          {/* Rows */}
          {rows.map((e,i)=>{
            const ev   = evBg(e.ev)
            const dist = distBg(e.dist)
            const la   = laBg(e.la)
            const res  = RESULT[e.res]||RESULT.OUT
            const traj = TRAJ[e.traj]||{fg:T.muted2}
            const ptc  = PITCH_COLOR[e.type]||T.muted2
            const rowBg = i%2===0 ? T.card : T.card2
            const isHR = e.res==="HR"

            return (
              <div key={i} style={{
                display:"grid",
                gridTemplateColumns:"56px 1fr 28px 64px 64px 50px 56px 50px 44px",
                background: isHR ? "#064E3B22" : rowBg,
                borderTop:`1px solid ${T.border}`,
                alignItems:"center",
              }}>
                {/* DATE */}
                <div style={{textAlign:"center",padding:"9px 4px",fontSize:12,color:T.muted2,fontFamily:"'SF Mono',monospace"}}>
                  {e.date}
                </div>
                {/* PITCHER */}
                <div style={{padding:"9px 8px",fontSize:13,fontWeight:600,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {e.pitcher}
                </div>
                {/* L/R */}
                <div style={{textAlign:"center",padding:"9px 2px",fontSize:11,color:T.muted2}}>
                  {e.lr}
                </div>
                {/* TYPE chip */}
                <div style={{textAlign:"center",padding:"9px 4px"}}>
                  <span style={{
                    display:"inline-block",
                    padding:"2px 6px",borderRadius:4,
                    background:`${ptc}22`,border:`1px solid ${ptc}44`,
                    fontSize:10,fontWeight:700,color:ptc,
                    letterSpacing:"0.03em",
                  }}>{e.type}</span>
                </div>
                {/* EV — green heat */}
                <div style={{
                  textAlign:"center",padding:"9px 4px",
                  background:ev.bg,
                  fontSize:14,fontWeight:800,
                  color:ev.fg,fontFamily:"'SF Mono',monospace",
                  borderLeft:`1px solid ${T.border}`,
                  borderRight:`1px solid ${T.border}`,
                }}>
                  {e.ev>0?e.ev.toFixed(1):"—"}
                </div>
                {/* LA */}
                <div style={{
                  textAlign:"center",padding:"9px 4px",
                  background:la.bg,
                  fontSize:13,color:la.fg,
                  fontFamily:"'SF Mono',monospace",
                  borderRight:`1px solid ${T.border}`,
                }}>
                  {e.la!=null?(e.la>0?"+"+e.la:e.la):"—"}
                </div>
                {/* DIST — green heat */}
                <div style={{
                  textAlign:"center",padding:"9px 4px",
                  background:dist.bg,
                  fontSize:14,fontWeight:e.dist>=350?800:400,
                  color:dist.fg,fontFamily:"'SF Mono',monospace",
                  borderRight:`1px solid ${T.border}`,
                }}>
                  {e.dist>0?e.dist:"—"}
                </div>
                {/* RES badge */}
                <div style={{
                  textAlign:"center",padding:"9px 4px",
                  background:res.bg,
                  borderRight:`1px solid ${T.border}`,
                }}>
                  <span style={{
                    fontSize:11,fontWeight:res.bold?900:600,
                    color:res.fg,letterSpacing:"0.06em",
                  }}>{e.res}</span>
                </div>
                {/* TRAJ */}
                <div style={{textAlign:"center",padding:"9px 4px",fontSize:11,fontWeight:600,color:traj.fg}}>
                  {e.traj}
                </div>
              </div>
            )
          })}
          </div>
         </div>
        </div>

        {/* EV legend */}
        <div style={{
          marginTop:10,display:"flex",gap:8,flexWrap:"wrap",
          padding:"8px 10px",background:T.card2,
          borderRadius:7,border:`1px solid ${T.border2}`,
        }}>
          <span style={{fontSize:9,color:T.muted2,fontWeight:700,letterSpacing:"0.1em",marginRight:2,textTransform:"uppercase",alignSelf:"center"}}>Exit Velo (mph)</span>
          {[
            ["108+","#064E3B","#00D97E"],
            ["100-107","#065F46","#34D399"],
            ["95-99","#0F2D1E","#6EE7B7"],
            ["<95",T.card2,"#6E7681"],
          ].map(([range,bg,fg])=>(
            <div key={range} style={{display:"flex",alignItems:"center",gap:5}}>
              <div style={{width:28,height:16,borderRadius:3,background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,color:fg,fontFamily:"monospace"}}>
                {range}
              </div>
            </div>
          ))}
          <span style={{marginLeft:"auto",fontSize:10,color:T.muted2}}>
            Launch Angle <span style={{color:"#34D399"}}>8–32°</span> = sweet spot
          </span>
        </div>

        {/* Column glossary — spell out the table abbreviations */}
        <div style={{marginTop:8,padding:"8px 10px",background:T.card2,borderRadius:7,border:`1px solid ${T.border2}`,fontSize:10,color:T.muted2,lineHeight:1.6}}>
          <b style={{color:T.muted}}>EV</b> Exit Velocity (mph) · <b style={{color:T.muted}}>LA</b> Launch Angle (°) · <b style={{color:T.muted}}>DIS</b> Distance (ft) · <b style={{color:T.muted}}>RES</b> Result · <b style={{color:T.muted}}>TRAJ</b> Trajectory (FB fly ball, GB ground ball, LD line drive, PU pop up)
        </div>
      </div>
    </div>
  )
}

export default function ContactLogTab() {
  const [query, setQuery]     = useState("")
  const [results, setResults] = useState([])
  const [selected, setSelected] = useState(null)   // {id, full_name}
  const [live, setLive]       = useState(null)      // API response
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)

  // Debounced player search
  useEffect(() => {
    if (!query || query.trim().length < 2) { setResults([]); return }
    const t = setTimeout(() => {
      searchPlayers(query.trim())
        .then(d => setResults(d?.players || []))
        .catch(() => setResults([]))
    }, 250)
    return () => clearTimeout(t)
  }, [query])

  // On open, default to the best-contact hitter across ALL of today's hitters.
  // Ranked by the season contact-quality metric already computed for every board
  // entry (one request, no per-player live pulls — fast), then load that hitter's
  // live contact log.
  useEffect(() => {
    let cancelled = false
    const cq = e => e?.matchup_fit?.contact_quality_fit_score ?? 0
    getLeaderboard('HRR_over_1.5', todayStr(), 200, false)
      .then(d => {
        if (cancelled) return
        const hitters = (d?.entries || []).filter(e => e.player_id)
        if (!hitters.length) return
        const best = hitters.reduce((a, b) => (cq(b) > cq(a) ? b : a))
        setSelected({ id: best.player_id, full_name: best.full_name })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Fetch contact log when a player is selected
  useEffect(() => {
    if (!selected) return
    setLoading(true); setError(null); setLive(null)
    getPlayerContactLog(selected.id)
      .then(d => setLive(d))
      .catch(e => setError(e.message || "fetch failed"))
      .finally(() => setLoading(false))
  }, [selected])

  const pick = (p) => { setSelected(p); setQuery(""); setResults([]) }
  const showLive = selected && live && (live.log?.length > 0)

  return (
    <div style={{
      background:"#080C12",minHeight:"100vh",paddingBottom:80,
      fontFamily:"-apple-system,'SF Pro Display',Arial,sans-serif",
      color:T.text,
    }}>
      {/* Tab header */}
      <div style={{
        padding:"14px 16px",
        borderBottom:`1px solid ${T.border}`,
        background:T.bg,
        position:"sticky",top:0,zIndex:10,
      }}>
        <div style={{fontWeight:900,fontSize:18,letterSpacing:"-0.03em"}}>
          Contact Log
        </div>
        <div style={{fontSize:11,color:T.muted2,marginTop:2}}>
          Statcast batted-ball history · EV / LA / distance
        </div>
      </div>

      {/* Player search */}
      <div style={{padding:"10px 14px",borderBottom:`1px solid ${T.border}`,background:T.bg,position:"relative"}}>
        <input
          value={query}
          onChange={e=>setQuery(e.target.value)}
          placeholder="Search a batter…"
          style={{
            width:"100%",boxSizing:"border-box",
            background:T.card,border:`1px solid ${T.border2}`,
            borderRadius:10,padding:"9px 12px",
            color:T.text,fontSize:13,outline:"none",
          }}
        />
        {results.length > 0 && (
          <div style={{
            position:"absolute",left:14,right:14,top:"100%",zIndex:20,
            background:T.card,border:`1px solid ${T.border2}`,
            borderRadius:10,marginTop:4,overflow:"hidden",
            maxHeight:240,overflowY:"auto",
            boxShadow:"0 8px 24px rgba(0,0,0,0.5)",
          }}>
            {results.map(p=>(
              <button key={p.id} onClick={()=>pick(p)} style={{
                display:"flex",justifyContent:"space-between",alignItems:"center",
                width:"100%",padding:"9px 12px",border:"none",
                borderBottom:`1px solid ${T.border}`,
                background:"transparent",color:T.text,
                fontSize:13,cursor:"pointer",textAlign:"left",
              }}>
                <span>{p.full_name}</span>
                <span style={{fontSize:11,color:T.muted2}}>{p.team} · {p.position}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main content */}
      <div style={{padding:"14px",maxWidth:520,margin:"0 auto"}}>
        {loading && (
          <div style={{textAlign:"center",padding:"40px 0",color:T.muted}}>
            Loading {selected?.full_name}…
          </div>
        )}

        {!loading && showLive && (
          <ContactLog playerName={live.name || selected.full_name} data={live} />
        )}

        {/* Selected player, but no live batted-ball data — honest empty state, never
            fabricated sample stats dressed up as this player's real Statcast. */}
        {!loading && selected && !showLive && (
          <div style={{
            textAlign:"center",padding:"28px 16px",color:T.muted,
            background:T.card,borderRadius:12,border:`1px solid ${T.border2}`,
            marginBottom:16,
          }}>
            {error
              ? `Couldn't load live data (${error}).`
              : `No batted balls for ${selected.full_name} in the last 15 days.`}
          </div>
        )}

        {/* Nothing selected yet — prompt to search, no placeholder data. */}
        {!loading && !selected && (
          <div style={{
            textAlign:"center",padding:"48px 20px",color:T.muted,
            background:T.card,borderRadius:12,border:`1px solid ${T.border2}`,
          }}>
            <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:6}}>Search a batter to begin</div>
            <div style={{fontSize:12.5,lineHeight:1.55,color:T.muted2,maxWidth:380,margin:"0 auto"}}>
              Type a hitter's name above to pull their <b style={{color:T.muted}}>real</b> Statcast batted-ball log — exit velo, launch angle, distance and result on every ball in play over the last 7 / 15 / 30 days.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
